import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Campaign as AnnouncementIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Groups as AudienceIcon,
  ImageOutlined as ImageIcon,
  PersonOutline as PanelIcon,
  Public as GlobalIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircleOutline as ActiveIcon,
  HighlightOff as InactiveIcon,
  Save as SaveIcon,
  School as SchoolIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import AnnouncementRichTextEditor, {
  stripAnnouncementHtml,
} from "../components/announcements/AnnouncementRichTextEditor";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const RECIPIENTS = [
  { value: "GLOBAL", label: "Global · all schools", short: "Global", color: crmColors.success.main },
  { value: "INDIVIDUAL", label: "Individual · selected schools", short: "Individual", color: crmColors.primary.main },
];

//Created By Cursor — legacy rows still readable in list
const LEGACY_RECIPIENTS = [
  { value: "ALL", label: "All schools (legacy)", short: "All", color: crmColors.secondary.main },
  { value: "BILLED", label: "Billed · Full System", short: "Billed", color: crmColors.success.main },
  { value: "BILLED_APP_ONLY", label: "Billed · App only", short: "App only", color: crmColors.info.main },
  { value: "NOT_BILLED", label: "Not billed", short: "Not billed", color: crmColors.energy.main },
];

const PANEL_KEYS = [
  ["admin_access", "Admin"],
  ["clerk_access", "Clerk"],
  ["teacher_access", "Teacher"],
  ["parents_access", "Parents"],
];

const emptyForm = () => ({
  id: 0,
  announcement_title: "",
  announcement: "",
  img_path: "",
  school_recipient: "GLOBAL",
  target_schools: [],
  admin_access: true,
  clerk_access: false,
  teacher_access: false,
  parents_access: false,
  status: "Active",
});

//Created By Cursor — form only uses GLOBAL | INDIVIDUAL
const normalizeRecipientType = (code) => {
  const v = String(code || "").toUpperCase();
  return v === "INDIVIDUAL" ? "INDIVIDUAL" : "GLOBAL";
};

//Created By Cursor
const parseTargetSchools = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") {
          const code = item.trim();
          return code ? { school_code: code, school_name: "" } : null;
        }
        const code = String(item?.school_code || "").trim();
        if (!code) return null;
        return {
          school_code: code,
          school_name: String(item?.school_name || "").trim(),
        };
      })
      .filter(Boolean);
  }
  return String(raw || "")
    .split(/[\s,;]+/)
    .map((code) => code.trim())
    .filter(Boolean)
    .map((school_code) => ({ school_code, school_name: "" }));
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

//Created By Cursor
const recipientMeta = (code) => {
  const v = String(code || "").toUpperCase();
  const mapped = v === "ALL" ? "GLOBAL" : v;
  return (
    RECIPIENTS.find((r) => r.value === mapped) ||
    LEGACY_RECIPIENTS.find((r) => r.value === v) || {
      value: v || "—",
      label: v || "—",
      short: v || "—",
      color: crmColors.secondary.main,
    }
  );
};

//Created By Cursor
const SummaryTile = ({ label, value, color, icon, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 1.6,
      height: "100%",
      borderRadius: "14px",
      cursor: onClick ? "pointer" : "default",
      border: `1px solid ${alpha(color, active ? 0.5 : 0.22)}`,
      background: `linear-gradient(145deg, ${alpha(color, active ? 0.16 : 0.08)} 0%, #fff 72%)`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      transition: "transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-1px)",
            borderColor: alpha(color, 0.45),
            boxShadow: `0 8px 18px ${alpha(color, 0.12)}`,
          }
        : undefined,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "11px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(color, 0.14),
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: crmColors.text.secondary,
          lineHeight: 1.15,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 20, color: crmColors.text.primary, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

//Created By Cursor
const PanelToggle = ({ label, checked, onChange }) => (
  <Box
    onClick={() => onChange(!checked)}
    sx={{
      px: 1.35,
      py: 0.9,
      borderRadius: "10px",
      cursor: "pointer",
      userSelect: "none",
      border: `1px solid ${
        checked ? alpha(crmColors.primary.main, 0.45) : crmColors.border.default
      }`,
      bgcolor: checked ? alpha(crmColors.primary.main, 0.12) : "#fff",
      color: checked ? crmColors.primary.main : crmColors.text.secondary,
      fontWeight: 700,
      fontSize: 12.5,
      boxShadow: checked ? `0 2px 8px ${alpha(crmColors.primary.main, 0.14)}` : "none",
      transition: "all 0.15s ease",
      "&:hover": {
        borderColor: alpha(crmColors.primary.main, 0.45),
        bgcolor: alpha(crmColors.primary.main, checked ? 0.16 : 0.05),
      },
    }}
  >
    {label}
  </Box>
);

//Created By Cursor
const DialogSection = ({ step, title, hint, children }) => (
  <Box
    sx={{
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: "14px",
      border: `1px solid ${crmColors.border.default}`,
      background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.03)} 0%, ${crmColors.background.paper} 100%)`,
      boxShadow: `0 1px 0 ${alpha("#fff", 0.8)} inset`,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "7px",
          display: "grid",
          placeItems: "center",
          fontSize: 11,
          fontWeight: 800,
          color: "#fff",
          background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`,
          boxShadow: `0 2px 6px ${alpha(crmColors.primary.main, 0.28)}`,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: crmColors.text.primary,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
    </Stack>
    {children}
  </Box>
);

//Created By Cursor — manage tm_crm_announcement
const Announcements = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [recipientFilter, setRecipientFilter] = useState("ALL");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [schoolSearchLoading, setSchoolSearchLoading] = useState(false);
  const schoolSearchTimer = React.useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getCrmAnnouncementList({
      status: statusFilter,
      school_recipient: recipientFilter,
      q,
      limit: 300,
    });
    setLoading(false);
    if (res.success) {
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotal(typeof res.total === "number" ? res.total : 0);
    } else {
      toast.error(res.error || res.message || "Failed to load announcements");
      setRows([]);
      setTotal(0);
    }
  }, [statusFilter, recipientFilter, q]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => String(r.status || "").toLowerCase() === "active").length;
    const inactive = rows.length - active;
    const withImage = rows.filter((r) => String(r.img_path || "").trim()).length;
    return { active, inactive, withImage, total };
  }, [rows, total]);

  //Created By Cursor — school picker for INDIVIDUAL audience
  const searchTargetSchools = useCallback(async (keyword) => {
    const q = String(keyword || "").trim();
    if (q.length < 2) {
      setSchoolOptions([]);
      return;
    }
    setSchoolSearchLoading(true);
    const result = await apiService.searchSchools(q, [
      "school_name",
      "school_code",
      "admin_name",
      "admin_phone",
    ]);
    setSchoolSearchLoading(false);
    if (!result.success) {
      setSchoolOptions([]);
      return;
    }
    const list = Array.isArray(result.data)
      ? result.data
      : Array.isArray(result.data?.schools)
        ? result.data.schools
        : [];
    setSchoolOptions(
      list
        .map((s) => ({
          school_code: String(s.school_code || s.School_Code || "").trim(),
          school_name: String(s.school_name || s.School_Name || "").trim(),
        }))
        .filter((s) => s.school_code)
    );
  }, []);

  const onSchoolInputChange = (_e, value, reason) => {
    if (reason === "reset") return;
    if (schoolSearchTimer.current) clearTimeout(schoolSearchTimer.current);
    schoolSearchTimer.current = setTimeout(() => {
      searchTargetSchools(value);
    }, 280);
  };

  const openCreate = () => {
    setForm(emptyForm());
    setSchoolOptions([]);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: Number(row.id) || 0,
      announcement_title: row.announcement_title || "",
      announcement: row.announcement || "",
      img_path: row.img_path || "",
      school_recipient: normalizeRecipientType(row.type || row.school_recipient),
      target_schools:
        normalizeRecipientType(row.type || row.school_recipient) === "INDIVIDUAL"
          ? parseTargetSchools(row.target_school_codes)
          : [],
      admin_access: !!Number(row.admin_access),
      clerk_access: !!Number(row.clerk_access),
      teacher_access: !!Number(row.teacher_access),
      parents_access: !!Number(row.parents_access),
      status: row.status === "InActive" ? "InActive" : "Active",
    });
    setSchoolOptions([]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.announcement_title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (
      !form.admin_access &&
      !form.clerk_access &&
      !form.teacher_access &&
      !form.parents_access
    ) {
      toast.error("Select at least one panel access");
      return;
    }
    if (
      form.school_recipient === "INDIVIDUAL" &&
      (!Array.isArray(form.target_schools) || form.target_schools.length === 0)
    ) {
      toast.error("Select at least one school");
      return;
    }
    setSaving(true);
    const res = await apiService.upsertCrmAnnouncement({
      id: form.id || 0,
      announcement_title: form.announcement_title.trim(),
      announcement: form.announcement,
      img_path: form.img_path.trim(),
      type: form.school_recipient,
      school_recipient: form.school_recipient,
      target_school_codes: (form.target_schools || []).map((s) => s.school_code),
      admin_access: form.admin_access ? 1 : 0,
      clerk_access: form.clerk_access ? 1 : 0,
      teacher_access: form.teacher_access ? 1 : 0,
      parents_access: form.parents_access ? 1 : 0,
      status: form.status,
    });
    setSaving(false);
    if (!res.success) {
      toast.error(res.error || "Save failed");
      return;
    }
    toast.success(form.id ? "Announcement updated" : "Announcement created");
    setDialogOpen(false);
    fetchList();
  };

  const handleDeactivate = async (row) => {
    if (!window.confirm(`Deactivate “${row.announcement_title || row.id}”?`)) {
      return;
    }
    const res = await apiService.deleteCrmAnnouncement({ id: row.id, hard: false });
    if (!res.success) {
      toast.error(res.error || "Failed to deactivate");
      return;
    }
    toast.success("Announcement deactivated");
    fetchList();
  };

  //Created By Cursor — permanent remove from tm_crm_announcement
  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Permanently delete “${row.announcement_title || row.id}”? This cannot be undone.`
      )
    ) {
      return;
    }
    const res = await apiService.deleteCrmAnnouncement({ id: row.id, hard: true });
    if (!res.success) {
      toast.error(res.error || "Failed to delete");
      return;
    }
    toast.success("Announcement deleted");
    if (dialogOpen && Number(form.id) === Number(row.id)) {
      setDialogOpen(false);
    }
    fetchList();
  };

  const formAudience = recipientMeta(form.school_recipient);
  const formPanels = PANEL_KEYS.filter(([key]) => form[key]).map(([, label]) => label);

  const actionBtnSx = {
    textTransform: "none",
    borderRadius: "8px",
    fontWeight: 700,
    color: "#fff",
    borderColor: "#AED581",
    bgcolor: alpha("#0C7300", 0.42),
    "&:hover": {
      color: "#fff",
      borderColor: "#AED581",
      bgcolor: alpha("#0C7300", 0.42),
    },
    "&.Mui-disabled": {
      color: alpha("#fff", 0.45),
      borderColor: alpha("#89BE4C", 0.35),
      bgcolor: alpha("#0C7300", 0.18),
    },
  };

  const deleteGlowSx = {
    textTransform: "none",
    borderRadius: "10px",
    fontWeight: 700,
    color: "#fff",
    border: `1px solid ${alpha("#F9A602", 0.4)}`,
    bgcolor: "#A52A2A",
    backgroundImage: "none",
    boxShadow: `0 0 8px ${alpha("#F9A602", 0.28)}, 0 2px 6px ${alpha("#A52A2A", 0.3)}`,
    animation: "eduokDeleteGoldGlow 2.8s ease-in-out infinite",
    "@keyframes eduokDeleteGoldGlow": {
      "0%, 100%": {
        boxShadow: `0 0 6px ${alpha("#F9A602", 0.22)}, 0 2px 6px ${alpha("#A52A2A", 0.28)}`,
      },
      "50%": {
        boxShadow: `0 0 10px ${alpha("#F9A602", 0.38)}, 0 3px 8px ${alpha("#A52A2A", 0.34)}`,
      },
    },
    "&:hover": {
      color: "#fff",
      borderColor: alpha("#F9A602", 0.65),
      bgcolor: "#7A1F1F",
      backgroundImage: "none",
      boxShadow: `0 0 12px ${alpha("#F9A602", 0.42)}, 0 3px 10px ${alpha("#A52A2A", 0.38)}`,
    },
    "&.Mui-disabled": {
      color: alpha("#fff", 0.55),
      bgcolor: alpha("#A52A2A", 0.45),
      animation: "none",
      boxShadow: "none",
    },
  };

  return (
    <PageContentLayout
      title="Announcements"
      subtitle="Dashboard banners for Admin, Clerk, Teacher & Parents"
      barSubtitle={`${total} announcement${total === 1 ? "" : "s"}`}
      breadcrumbs={[
        {
          label: "Announcements",
          icon: <AnnouncementIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/dashboard"
      darkBar
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchList}
            disabled={loading}
            sx={actionBtnSx}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={openCreate}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 800,
              px: 1.75,
              background: crmColors.energy.main,
              backgroundImage: "none",
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.55)}`,
              boxShadow: `0 4px 12px ${alpha(crmColors.energy.main, 0.4)}`,
              "&:hover": {
                background: crmColors.energy.dark,
                backgroundImage: "none",
                boxShadow: `0 6px 16px ${alpha(crmColors.energy.main, 0.48)}`,
              },
            }}
          >
            Add announcement
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1.75}>
        <Grid container spacing={1.25}>
          <Grid item xs={6} sm={3}>
            <SummaryTile
              label="Total"
              value={stats.total}
              color={crmColors.primary.main}
              icon={<AnnouncementIcon sx={{ fontSize: 20 }} />}
              active={statusFilter === "ALL" && recipientFilter === "ALL"}
              onClick={() => {
                setStatusFilter("ALL");
                setRecipientFilter("ALL");
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryTile
              label="Active"
              value={stats.active}
              color={crmColors.success.main}
              icon={<ActiveIcon sx={{ fontSize: 20 }} />}
              active={statusFilter === "Active"}
              onClick={() => setStatusFilter("Active")}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryTile
              label="Inactive"
              value={stats.inactive}
              color={crmColors.secondary.main}
              icon={<InactiveIcon sx={{ fontSize: 20 }} />}
              active={statusFilter === "InActive"}
              onClick={() => setStatusFilter("InActive")}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryTile
              label="With image"
              value={stats.withImage}
              color={crmColors.energy.main}
              icon={<ImageIcon sx={{ fontSize: 20 }} />}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            p: 1.5,
            borderRadius: "14px",
            border: `1px solid ${crmColors.border.default}`,
            background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.04)} 0%, ${crmColors.background.paper} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ md: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search title or message…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, minWidth: { md: 280 }, flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ ...fieldSx, minWidth: 140 }}
            >
              <MenuItem value="ALL">All statuses</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="InActive">Inactive</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Recipient type"
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
              sx={{ ...fieldSx, minWidth: 190 }}
            >
              <MenuItem value="ALL">All types</MenuItem>
              {RECIPIENTS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.short}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
            <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.02),
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: "auto",
                mb: 1.25,
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`,
                color: "#fff",
                boxShadow: `0 8px 18px ${alpha(crmColors.primary.main, 0.28)}`,
              }}
            >
              <AnnouncementIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: crmColors.text.primary }}>
              No announcements yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Create a banner for billed, app-only, or not-billed schools
            </Typography>
            <Button
              variant="contained"
              size="small"
              disableElevation
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={openCreate}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 800,
                px: 1.75,
                background: crmColors.energy.main,
                backgroundImage: "none",
                color: "#fff",
                boxShadow: `0 4px 12px ${alpha(crmColors.energy.main, 0.35)}`,
                "&:hover": {
                  background: crmColors.energy.dark,
                  backgroundImage: "none",
                },
              }}
            >
              Add announcement
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.15}>
            {rows.map((row) => {
              const audience = recipientMeta(row.type || row.school_recipient);
              const targetSchools = parseTargetSchools(row.target_school_codes);
              const panels = PANEL_KEYS.filter(([key]) => Number(row[key])).map(([, label]) => label);
              const active = String(row.status || "").toLowerCase() === "active";
              const added = row.date_added
                ? new Date(row.date_added).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "—";

              return (
                <Box
                  key={row.id}
                  sx={{
                    display: "flex",
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: `1px solid ${crmColors.border.default}`,
                    background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                      audience.color,
                      0.05
                    )} 100%)`,
                    transition: "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: alpha(crmColors.primary.main, 0.32),
                      boxShadow: `0 8px 22px ${alpha(crmColors.primary.main, 0.1)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 4, sm: 5 },
                      alignSelf: "stretch",
                      background: active
                        ? `linear-gradient(180deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.mid} 55%, ${crmColors.logo.light} 100%)`
                        : alpha(crmColors.secondary.main, 0.35),
                      flexShrink: 0,
                    }}
                  />

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: { xs: 1.25, md: 2 },
                      px: { xs: 1.35, sm: 1.75 },
                      py: 1.35,
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        background: active
                          ? `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`
                          : alpha(crmColors.secondary.main, 0.14),
                        color: active ? "#fff" : crmColors.text.secondary,
                        boxShadow: active
                          ? `0 4px 12px ${alpha(crmColors.primary.main, 0.28)}`
                          : "none",
                      }}
                    >
                      <AnnouncementIcon sx={{ fontSize: 20 }} />
                    </Box>

                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: 14.5,
                            color: crmColors.text.primary,
                            lineHeight: 1.25,
                          }}
                        >
                          {row.announcement_title || "Untitled"}
                        </Typography>
                        <Chip
                          size="small"
                          label={active ? "Active" : "Inactive"}
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: 10.5,
                            bgcolor: active
                              ? alpha(crmColors.success.main, 0.12)
                              : alpha(crmColors.secondary.main, 0.12),
                            color: active ? crmColors.success.dark : crmColors.text.secondary,
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                      </Stack>
                      {stripAnnouncementHtml(row.announcement) ? (
                        <Box
                          sx={{
                            mt: 0.35,
                            fontSize: 12.5,
                            color: crmColors.text.secondary,
                            lineHeight: 1.45,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            "& p": { m: 0, mb: 0.35, "&:last-child": { mb: 0 } },
                            "& ul, & ol": { m: 0, pl: 2.1, mb: 0.35 },
                            "& li": { mb: 0.15 },
                            "& strong, & b": { fontWeight: 800, color: crmColors.text.primary },
                            "& em, & i": { fontStyle: "italic" },
                            "& u": { textDecoration: "underline" },
                            "& s, & strike": { textDecoration: "line-through" },
                            "& a": { color: crmColors.primary.main, fontWeight: 600 },
                            "& h1, & h2, & h3": {
                              m: 0,
                              mb: 0.35,
                              fontSize: "inherit",
                              fontWeight: 800,
                              color: crmColors.text.primary,
                            },
                          }}
                          dangerouslySetInnerHTML={{ __html: row.announcement }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontSize: 12.5,
                            color: crmColors.text.disabled,
                            lineHeight: 1.4,
                          }}
                        >
                          No message body
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.85 }}
                      >
                        <Chip
                          size="small"
                          icon={<AudienceIcon sx={{ fontSize: "14px !important" }} />}
                          label={
                            audience.value === "INDIVIDUAL" && targetSchools.length
                              ? `Individual · ${targetSchools.length}`
                              : audience.short
                          }
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor: alpha(audience.color, 0.12),
                            color: audience.color,
                            "& .MuiChip-icon": { color: audience.color },
                          }}
                        />
                        {audience.value === "INDIVIDUAL"
                          ? targetSchools.slice(0, 4).map((s) => (
                              <Chip
                                key={s.school_code}
                                size="small"
                                icon={<SchoolIcon sx={{ fontSize: "13px !important" }} />}
                                label={s.school_code}
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  bgcolor: alpha(crmColors.primary.main, 0.08),
                                  color: crmColors.primary.main,
                                  "& .MuiChip-icon": { color: crmColors.primary.main },
                                }}
                              />
                            ))
                          : null}
                        {audience.value === "INDIVIDUAL" && targetSchools.length > 4 ? (
                          <Chip
                            size="small"
                            label={`+${targetSchools.length - 4}`}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: 11,
                              bgcolor: alpha(crmColors.secondary.main, 0.1),
                              color: crmColors.text.secondary,
                            }}
                          />
                        ) : null}
                        {panels.map((p) => (
                          <Chip
                            key={p}
                            size="small"
                            icon={<PanelIcon sx={{ fontSize: "13px !important" }} />}
                            label={p}
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontWeight: 600,
                              fontSize: 11,
                              borderColor: alpha(crmColors.primary.main, 0.22),
                              color: crmColors.text.secondary,
                              "& .MuiChip-icon": { color: crmColors.primary.main },
                            }}
                          />
                        ))}
                        {row.img_path ? (
                          <Chip
                            size="small"
                            icon={<ImageIcon sx={{ fontSize: "13px !important" }} />}
                            label="Image"
                            sx={{
                              height: 22,
                              fontWeight: 600,
                              fontSize: 11,
                              bgcolor: alpha(crmColors.energy.main, 0.1),
                              color: crmColors.energy.dark,
                              "& .MuiChip-icon": { color: crmColors.energy.main },
                            }}
                          />
                        ) : null}
                        <Typography
                          sx={{ fontSize: 11.5, color: crmColors.text.disabled, fontWeight: 600 }}
                        >
                          {added}
                        </Typography>
                      </Stack>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ ml: { xs: 0, md: "auto" }, width: { xs: "100%", md: "auto" } }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEdit(row)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 700,
                          flex: { xs: 1, md: "none" },
                          color: "#fff",
                          borderColor: "#7851A9",
                          bgcolor: "#7851A9",
                          backgroundImage: "none",
                          boxShadow: `0 0 8px ${alpha("#7851A9", 0.4)}, 0 2px 6px ${alpha("#7851A9", 0.3)}`,
                          animation: "eduokEditPurpleGlow 2.8s ease-in-out infinite",
                          "@keyframes eduokEditPurpleGlow": {
                            "0%, 100%": {
                              boxShadow: `0 0 6px ${alpha("#7851A9", 0.32)}, 0 2px 6px ${alpha("#7851A9", 0.28)}`,
                            },
                            "50%": {
                              boxShadow: `0 0 10px ${alpha("#7851A9", 0.5)}, 0 3px 8px ${alpha("#7851A9", 0.36)}`,
                            },
                          },
                          "&:hover": {
                            color: "#fff",
                            borderColor: "#5E3D8A",
                            bgcolor: "#5E3D8A",
                            backgroundImage: "none",
                            boxShadow: `0 0 12px ${alpha("#7851A9", 0.55)}, 0 3px 10px ${alpha("#7851A9", 0.4)}`,
                          },
                        }}
                      >
                        Edit
                      </Button>
                      {active ? (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          startIcon={<InactiveIcon />}
                          onClick={() => handleDeactivate(row)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "10px",
                            fontWeight: 700,
                            flex: { xs: 1, md: "none" },
                            background: crmColors.energy.main,
                            backgroundImage: "none",
                            color: "#fff",
                            border: `1px solid ${alpha("#fff", 0.55)}`,
                            boxShadow: `0 4px 12px ${alpha(crmColors.energy.main, 0.4)}`,
                            "&:hover": {
                              background: crmColors.energy.dark,
                              backgroundImage: "none",
                              boxShadow: `0 6px 16px ${alpha(crmColors.energy.main, 0.48)}`,
                            },
                          }}
                        >
                          Deactivate
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(row)}
                        sx={{
                          ...deleteGlowSx,
                          flex: { xs: 1, md: "none" },
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
            m: { xs: 1.5, sm: 2 },
            width: { xs: "calc(100% - 24px)", sm: "100%" },
            border: `1px solid ${crmColors.border.default}`,
            boxShadow: `0 24px 48px ${alpha(crmColors.primary.darker, 0.18)}`,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 1.75,
            background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 48%, ${crmColors.hero.mid} 100%)`,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              left: -30,
              bottom: -50,
              width: 120,
              height: 120,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.05),
              pointerEvents: "none",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              right: -40,
              top: -50,
              width: 160,
              height: 160,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.06),
              pointerEvents: "none",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.22)}`,
              }}
            >
              <AnnouncementIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.2 }}>
                {form.id ? "Edit announcement" : "New announcement"}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.88, mt: 0.2 }} noWrap>
                {form.id
                  ? `Updating #${form.id} · ${form.school_recipient === "INDIVIDUAL" ? "Individual" : "Global"}`
                  : form.school_recipient === "INDIVIDUAL"
                    ? "Individual · select schools & compose"
                    : "Global · broadcast to all schools"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => !saving && setDialogOpen(false)}
            disabled={saving}
            sx={{
              color: "#fff",
              bgcolor: alpha("#fff", 0.12),
              border: `1px solid ${alpha("#fff", 0.18)}`,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
              "&:hover": { bgcolor: alpha("#fff", 0.22) },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          dividers={false}
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            px: { xs: 1.75, sm: 2.25 },
            py: 2,
            bgcolor: alpha(crmColors.primary.main, 0.02),
          }}
        >
          <Stack spacing={1.75}>
            <Box
              sx={{
                borderRadius: "12px",
                border: `1px solid ${crmColors.border.default}`,
                bgcolor: "#fff",
                px: 1.25,
                py: 1,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: crmColors.text.secondary,
                    flexShrink: 0,
                  }}
                >
                  Recipient
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={form.school_recipient === "INDIVIDUAL" ? "INDIVIDUAL" : "GLOBAL"}
                  onChange={(_e, next) => {
                    if (!next) return;
                    setForm((f) => ({
                      ...f,
                      school_recipient: next,
                      target_schools: next === "INDIVIDUAL" ? f.target_schools || [] : [],
                    }));
                  }}
                  sx={{
                    flexShrink: 0,
                    bgcolor: alpha(crmColors.primary.main, 0.04),
                    borderRadius: "9px",
                    p: 0.25,
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      borderRadius: "7px !important",
                      px: 1.25,
                      py: 0.35,
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: 12.5,
                      color: crmColors.text.secondary,
                      gap: 0.4,
                      "&.Mui-selected": {
                        bgcolor: crmColors.primary.main,
                        color: "#fff",
                        boxShadow: `0 2px 8px ${alpha(crmColors.primary.main, 0.28)}`,
                        "&:hover": { bgcolor: crmColors.primary.dark },
                      },
                      "&:hover": { bgcolor: alpha(crmColors.primary.main, 0.08) },
                    },
                  }}
                >
                  <ToggleButton value="GLOBAL">
                    <GlobalIcon sx={{ fontSize: 15 }} />
                    Global
                  </ToggleButton>
                  <ToggleButton value="INDIVIDUAL">
                    <SchoolIcon sx={{ fontSize: 15 }} />
                    Individual
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {form.school_recipient === "INDIVIDUAL" ? (
                <Box sx={{ mt: 1 }}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={schoolOptions}
                    loading={schoolSearchLoading}
                    value={form.target_schools || []}
                    onChange={(_e, next) =>
                      setForm((f) => ({ ...f, target_schools: next || [] }))
                    }
                    onInputChange={onSchoolInputChange}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(a, b) => a.school_code === b.school_code}
                    getOptionLabel={(opt) =>
                      opt.school_name
                        ? `${opt.school_code} · ${opt.school_name}`
                        : opt.school_code || ""
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.school_code}>
                        <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>
                          {option.school_code}
                          {option.school_name ? (
                            <Typography
                              component="span"
                              sx={{ fontWeight: 500, color: "text.secondary", ml: 0.75 }}
                            >
                              {option.school_name}
                            </Typography>
                          ) : null}
                        </Typography>
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.school_code}
                          size="small"
                          label={option.school_code}
                          sx={{ height: 22, fontWeight: 700, fontSize: 11 }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        autoFocus={!form.id}
                        placeholder="Search schools…"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {schoolSearchLoading ? (
                                <CircularProgress color="inherit" size={14} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                borderRadius: "14px",
                overflow: "hidden",
                border: `1px solid ${alpha(formAudience.color, 0.28)}`,
                boxShadow: `0 8px 20px ${alpha(formAudience.color, 0.1)}`,
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{
                  px: 1.75,
                  py: 1.2,
                  background: `linear-gradient(120deg, ${crmColors.primary.darker}, ${crmColors.logo.light})`,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.3, minWidth: 0 }}>
                  {form.announcement_title.trim() || "Announcement title"}
                </Typography>
                <Chip
                  size="small"
                  label={form.status === "Active" ? "Active" : "Inactive"}
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    fontSize: 10.5,
                    flexShrink: 0,
                    bgcolor: alpha("#fff", 0.18),
                    color: "#fff",
                    border: `1px solid ${alpha("#fff", 0.28)}`,
                  }}
                />
              </Box>
              <Box sx={{ px: 1.75, py: 1.35 }}>
                {stripAnnouncementHtml(form.announcement) ? (
                  <Box
                    sx={{
                      fontSize: 13,
                      color: crmColors.text.secondary,
                      lineHeight: 1.45,
                      maxHeight: 88,
                      overflow: "hidden",
                      minHeight: 40,
                      "& p": { m: 0, mb: 0.5 },
                      "& ul, & ol": { m: 0, pl: 2.25 },
                      "& a": { color: crmColors.primary.main },
                    }}
                    dangerouslySetInnerHTML={{ __html: form.announcement }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: crmColors.text.disabled,
                      lineHeight: 1.4,
                      minHeight: 40,
                    }}
                  >
                    Message preview updates as you type…
                  </Typography>
                )}
                <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap sx={{ mt: 1.1 }}>
                  <Chip
                    size="small"
                    icon={<AudienceIcon sx={{ fontSize: "14px !important" }} />}
                    label={
                      formAudience.value === "INDIVIDUAL" && form.target_schools?.length
                        ? `Individual · ${form.target_schools.length}`
                        : formAudience.short
                    }
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      fontSize: 11,
                      bgcolor: alpha(formAudience.color, 0.12),
                      color: formAudience.color,
                      "& .MuiChip-icon": { color: formAudience.color },
                    }}
                  />
                  {formAudience.value === "INDIVIDUAL"
                    ? (form.target_schools || []).slice(0, 5).map((s) => (
                        <Chip
                          key={s.school_code}
                          size="small"
                          icon={<SchoolIcon sx={{ fontSize: "13px !important" }} />}
                          label={s.school_code}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor: alpha(crmColors.primary.main, 0.08),
                            color: crmColors.primary.main,
                            "& .MuiChip-icon": { color: crmColors.primary.main },
                          }}
                        />
                      ))
                    : null}
                  {(formPanels.length ? formPanels : ["No panels"]).map((p) => (
                    <Chip
                      key={p}
                      size="small"
                      icon={<PanelIcon sx={{ fontSize: "13px !important" }} />}
                      label={p}
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: 11,
                        bgcolor: alpha(crmColors.primary.main, 0.08),
                        color: crmColors.primary.main,
                        "& .MuiChip-icon": { color: crmColors.primary.main },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>

            <DialogSection step="1" title="Content" hint="Title, formatted message & optional image">
              <Stack spacing={1.5}>
                <TextField
                  label="Title"
                  size="small"
                  required
                  fullWidth
                  autoFocus={!form.id && form.school_recipient !== "INDIVIDUAL"}
                  value={form.announcement_title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, announcement_title: e.target.value }))
                  }
                  sx={fieldSx}
                />
                <AnnouncementRichTextEditor
                  value={form.announcement}
                  onChange={(html) => setForm((f) => ({ ...f, announcement: html }))}
                />
                <TextField
                  label="Image path / URL"
                  size="small"
                  fullWidth
                  value={form.img_path}
                  onChange={(e) => setForm((f) => ({ ...f, img_path: e.target.value }))}
                  helperText="Optional banner image for the school app"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ImageIcon sx={{ fontSize: 18, color: crmColors.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Stack>
            </DialogSection>

            <DialogSection step="2" title="Panels & status" hint="Where it appears">
              <Stack spacing={1.5}>
                <TextField
                  select
                  label="Status"
                  size="small"
                  fullWidth
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  sx={fieldSx}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="InActive">Inactive</MenuItem>
                </TextField>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: crmColors.text.secondary,
                      mb: 0.85,
                    }}
                  >
                    Show on panels
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {PANEL_KEYS.map(([key, label]) => (
                      <PanelToggle
                        key={key}
                        label={label}
                        checked={!!form[key]}
                        onChange={(next) => setForm((f) => ({ ...f, [key]: next }))}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </DialogSection>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${crmColors.border.default}`,
            bgcolor: alpha(crmColors.primary.main, 0.03),
            justifyContent: "space-between",
          }}
        >
          <Box>
            {form.id ? (
              <Button
                variant="outlined"
                disabled={saving}
                startIcon={<DeleteIcon />}
                onClick={() =>
                  handleDelete({
                    id: form.id,
                    announcement_title: form.announcement_title,
                  })
                }
                sx={deleteGlowSx}
              >
                Delete
              </Button>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                borderColor: crmColors.border.hover,
                color: crmColors.text.primary,
                bgcolor: "#fff",
                "&:hover": {
                  borderColor: crmColors.secondary.main,
                  bgcolor: alpha(crmColors.secondary.main, 0.06),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : form.id ? (
                  <SaveIcon />
                ) : (
                  <AddIcon />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "10px",
                px: 2.5,
                color: "#fff",
                bgcolor: crmColors.energy.main,
                backgroundImage: "none",
                boxShadow: `0 6px 16px ${alpha(crmColors.energy.main, 0.35)}`,
                "&:hover": { bgcolor: crmColors.energy.dark },
                "&.Mui-disabled": {
                  bgcolor: alpha(crmColors.energy.main, 0.4),
                  color: "#fff",
                },
              }}
            >
              {saving ? "Saving…" : form.id ? "Save changes" : "Create announcement"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default Announcements;
